import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import backendUrl from '../config';
import { logout } from './authSlice';

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  role: 'admin' | 'user';
  is_active: boolean;
  created_at: string;
  avatar?: string;
  preferences?: {
    email_notifications: boolean;
    theme: 'light' | 'dark';
    language: string;
  };
}

export interface Feedback {
  id: string;
  user_id: string;
  rating: number;
  comment: string;
  category: 'bug' | 'feature' | 'general';
  created_at: string;
  status: 'pending' | 'reviewed' | 'implemented' | 'resolved';
  attachments?: Array<{
    filename: string;
    url: string;
    content_type?: string;
    size?: number;
  }>;
  github_issue_number?: number;
  github_issue_url?: string;
}

interface UsersState {
  profile: UserProfile | null;
  feedback: Feedback[];
  loading: boolean;
  error: string | null;
}

const initialState: UsersState = {
  profile: null,
  feedback: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchProfile = createAsyncThunk(
  'users/fetchProfile',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/users/me`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        try {
          sessionStorage.setItem(
            'sessionExpiredMessage',
            'Your session has expired. Please log in again to continue.'
          );
        } catch {}
        dispatch(logout());
        setTimeout(() => { window.location.href = '/login'; }, 100);
        return rejectWithValue('Session expired. Redirecting to login...');
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'users/updateProfile',
  async (profileData: Partial<UserProfile>, { getState, rejectWithValue, dispatch }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.put(`${backendUrl}/api/users/profile`, profileData, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      
      // If a new token is provided (email was changed), update the auth state
      if (response.data.access_token) {
        localStorage.setItem('aihub_token', response.data.access_token);
        // Import and dispatch setToken action from authSlice
        const { setToken } = await import('./authSlice');
        dispatch(setToken(response.data.access_token));
      }
      
      return response.data;
    } catch (error: any) {
      // Handle structured error responses
      if (error.response?.data?.detail && typeof error.response.data.detail === 'object') {
        return rejectWithValue(error.response.data.detail);
      }
      return rejectWithValue({
        message: 'Failed to update profile',
        errors: { general: ['An unexpected error occurred. Please try again later.'] }
      });
    }
  }
);

export const submitFeedback = createAsyncThunk(
  'users/submitFeedback',
  async (
    feedbackData: { rating: number; comment: string; category: 'bug' | 'feature' | 'general'; images?: File[] },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const form = new FormData();
      form.append('rating', String(feedbackData.rating));
      form.append('comment', feedbackData.comment || '');
      form.append('category', feedbackData.category);
      (feedbackData.images || []).forEach((f) => form.append('images', f));

      const response = await axios.post(`${backendUrl}/api/users/feedback`, form, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        try {
          sessionStorage.setItem(
            'sessionExpiredMessage',
            'Your session has expired. Please log in again to submit feedback.'
          );
        } catch {}
        dispatch(logout());
        setTimeout(() => { window.location.href = '/login'; }, 100);
        return rejectWithValue('Session expired. Redirecting to login...');
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to submit feedback');
    }
  }
);

export const fetchFeedback = createAsyncThunk(
  'users/fetchFeedback',
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: { token: string } };
      const response = await axios.get(`${backendUrl}/api/users/feedback`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        try {
          sessionStorage.setItem(
            'sessionExpiredMessage',
            'Your session has expired. Please log in again to continue.'
          );
        } catch {}
        dispatch(logout());
        setTimeout(() => { window.location.href = '/login'; }, 100);
        return rejectWithValue('Session expired. Redirecting to login...');
      }
      return rejectWithValue(error.response?.data?.detail || 'Failed to fetch feedback');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        // Backend returns user data nested under 'user' key
        state.profile = action.payload.user || action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Submit Feedback
      .addCase(submitFeedback.fulfilled, (state, action) => {
        state.feedback.unshift(action.payload);
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Fetch Feedback
      .addCase(fetchFeedback.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeedback.fulfilled, (state, action) => {
        state.loading = false;
        state.feedback = action.payload;
      })
      .addCase(fetchFeedback.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = usersSlice.actions;
export default usersSlice.reducer;
